import axios from 'axios';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Modalheader from 'components/modal/components/header';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import useModal from 'components/modal/useModal';
import Button from 'components/button';
import { TrashIcon } from 'assets/icons';

const ModalCreateJiraBug = () => {
  const { toggle } = useModal();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    loomLink: ''
  });
  const [photos, setPhotos] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFiles = (files) => {
    const imageFiles = Array.from(files).filter((file) =>
      file.type.startsWith('image/')
    );
    const newPhotos = imageFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setPhotos((prev) => [...prev, ...newPhotos]);
  };

  const handlePhotoUpload = (e) => {
    handleFiles(e.target.files);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removePhoto = (index) => {
    setPhotos((prev) => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description || isSubmitting) return;

    setIsSubmitting(true);
    const body = new FormData();
    body.append('title', formData.title);
    body.append('description', formData.description);
    if (formData.loomLink) body.append('loomLink', formData.loomLink);
    photos.forEach((p) => body.append('attachments', p.file, p.name));

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}admin/createJiraBug`,
        body
      );
      toast.success(
        <div>
          Ticket créé :{' '}
          <a href={data.url} target="_blank" rel="noopener noreferrer" className="underline font-medium">
            {data.key}
          </a>
        </div>,
        { autoClose: 6000 }
      );
      photos.forEach((photo) => URL.revokeObjectURL(photo.preview));
      toggle();
    } catch (err) {
      const message = err?.response?.data?.message || 'Erreur lors de la création du ticket.';
      console.error('Jira bug submit:', err?.response?.data || err);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Signaler un bug
      </Modalheader>
      <ModalBody>
        <form className="flex flex-col w-full gap-4">
          <div>
            <label htmlFor="title" className="ft-sm font-medium text-gray-700">
              Titre du bug <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full h-10 px-3 ft-b placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              name="title"
              placeholder="Ex: Erreur lors de la création d'une mission"
              onChange={handleChange}
              value={formData.title}
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="ft-sm font-medium text-gray-700">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full min-h-[120px] px-3 py-2 ft-b placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              name="description"
              placeholder="Décrivez le bug en détail..."
              onChange={handleChange}
              value={formData.description}
              required
            />
          </div>

          <div>
            <label htmlFor="loomLink" className="ft-sm font-medium text-gray-700">
              Lien Loom (optionnel)
            </label>
            <input
              className="w-full h-10 px-3 ft-b placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
              name="loomLink"
              placeholder="https://www.loom.com/share/..."
              onChange={handleChange}
              value={formData.loomLink}
            />
          </div>

          <div>
            <label htmlFor="photos" className="ft-sm font-medium text-gray-700 block mb-2">
              Captures d'écran
            </label>
            
            <div
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging
                  ? 'border-yellow-500 bg-yellow-50'
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400'
              }`}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
                id="photo-upload"
              />
              <div className="flex flex-col items-center gap-2">
                <svg
                  className="w-10 h-10 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <div>
                  <label
                    htmlFor="photo-upload"
                    className="ft-sm font-medium text-yellow-600 cursor-pointer hover:text-yellow-700"
                  >
                    Cliquez pour choisir
                  </label>
                  <span className="ft-sm text-gray-600"> ou glissez-déposez vos images ici</span>
                </div>
                <p className="ft-xs text-gray-500">PNG, JPG, GIF jusqu'à 10MB</p>
              </div>
            </div>
            
            {photos.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                {photos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={photo.preview}
                      alt={`Capture ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <TrashIcon wh={16} color="#fff" />
                    </button>
                    <p className="ft-xs text-gray-600 mt-1 truncate">{photo.name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button onClick={toggle} size="LG" color="SECONDARY" disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          size="LG"
          color={isSubmitting || !formData.title || !formData.description ? 'DISABLED' : 'PRIMARY'}
          disabled={isSubmitting || !formData.title || !formData.description}
        >
          {isSubmitting ? 'Envoi…' : 'Envoyer'}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalCreateJiraBug;
